import React, { useEffect, useRef, useState } from 'react';
import ProfileFormSheet from '../modals/ProfileFormSheet';
import { useAuth } from '../../contexts/AuthContext';
import { uploadFileToS3 } from '../../utils/s3ClientUpload';
import '../../pages/UserProfileView.css';

const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,image/svg+xml';

function isImageDocument(doc) {
  if (!doc) return false;
  if (String(doc.media_type || '').toLowerCase() === 'image') return true;
  const mime = String(doc.mime_type || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  const name = String(doc.file_name || doc.url || '').toLowerCase();
  return /\.(png|jpe?g|gif|webp|svg|bmp)(\?|$)/.test(name);
}

async function saveGroupLogoUrl(groupId, url) {
  const putRes = await fetch(`/api/groups/${groupId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ logo_url: url })
  });
  const putData = await putRes.json();
  if (!putData.success) {
    throw new Error(putData.message || 'Failed to save logo');
  }
  return url;
}

/**
 * Popup to set an organization logo: upload file, paste link, or pick an image
 * from My Uploads (images on the current enrollment).
 */
const GroupLogoPickerSheet = ({
  open,
  onClose,
  groupId,
  groupName = 'organization',
  currentLogoUrl = '',
  onSaved,
  onError
}) => {
  const { currentRole, user, fetchWithAuth } = useAuth();
  const ugpId = currentRole?.id || null;
  const fileInputRef = useRef(null);

  const [tab, setTab] = useState('upload');
  const [selectedFile, setSelectedFile] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState('');
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!open) return;
    setTab('upload');
    setSelectedFile(null);
    setLinkUrl(String(currentLogoUrl || '').trim());
    setSelectedDocId('');
    setLocalError('');
    setSaving(false);
  }, [open, currentLogoUrl]);

  useEffect(() => {
    if (!open || tab !== 'library' || !ugpId) return undefined;
    let cancelled = false;
    (async () => {
      setDocsLoading(true);
      try {
        const res = await fetch(
          `/api/documents?user_groups_periods_id=${encodeURIComponent(ugpId)}`
        );
        const data = await res.json();
        if (cancelled) return;
        if (data.success) {
          setDocs((data.data || []).filter(isImageDocument));
        } else {
          setDocs([]);
        }
      } catch {
        if (!cancelled) setDocs([]);
      } finally {
        if (!cancelled) setDocsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, tab, ugpId]);

  const rememberAsDocument = async (payload) => {
    if (!ugpId || !fetchWithAuth) return;
    try {
      await fetchWithAuth('/api/documents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_groups_periods_id: ugpId,
          parent_table: 'user_groups_periods',
          parent_id: ugpId,
          title: payload.title || `${groupName} logo`,
          url: payload.url,
          storage_kind: payload.storageKind,
          media_type: 'image',
          mime_type: payload.mimeType || null,
          file_name: payload.fileName || null,
          file_size_bytes: payload.fileSizeBytes || null,
          category: 'org_logo',
          created_by: user?.id || undefined,
          metadata: { purpose: 'org_logo', group_id: String(groupId) }
        })
      });
    } catch {
      // Logo save still succeeds even if library insert fails (e.g. pre-migration).
    }
  };

  const handleApply = async () => {
    setLocalError('');
    setSaving(true);
    try {
      let url = '';

      if (tab === 'upload') {
        if (!selectedFile) {
          throw new Error('Choose an image file to upload.');
        }
        const uploaded = await uploadFileToS3(selectedFile, {
          fileLocation: 'org-logos',
          customFilename: String(groupId)
        });
        url = uploaded.url;
        await saveGroupLogoUrl(groupId, url);
        await rememberAsDocument({
          title: selectedFile.name || `${groupName} logo`,
          url,
          storageKind: 's3',
          mimeType: uploaded.mimeType,
          fileName: uploaded.fileName,
          fileSizeBytes: uploaded.fileSizeBytes
        });
      } else if (tab === 'link') {
        url = String(linkUrl || '').trim();
        if (!url) throw new Error('Enter an image URL.');
        if (!/^https?:\/\//i.test(url)) {
          throw new Error('URL must start with http:// or https://');
        }
        await saveGroupLogoUrl(groupId, url);
      } else {
        const doc = docs.find((d) => String(d.id) === String(selectedDocId));
        if (!doc?.url) throw new Error('Select an image from My Uploads.');
        url = String(doc.url).trim();
        await saveGroupLogoUrl(groupId, url);
      }

      onSaved?.(url);
      onClose?.();
    } catch (err) {
      const msg = err.message || 'Could not update logo.';
      setLocalError(msg);
      onError?.(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProfileFormSheet
      open={open}
      onClose={() => {
        if (!saving) onClose?.();
      }}
      closeDisabled={saving}
      title={`Logo — ${groupName}`}
      titleId="group-logo-picker-title"
      subtext="Upload a file, paste a link, or reuse something from My Uploads."
      panelClassName="profile-sheet-panel--form"
      footer={
        <button
          type="button"
          className="profile-sheet-btn profile-sheet-btn--primary"
          disabled={saving}
          onClick={handleApply}
        >
          {saving ? 'Saving…' : 'Use logo'}
        </button>
      }
    >
      <div className="profile-doc-source groups-logo-picker">
        <div className="profile-doc-source-tabs" role="tablist" aria-label="Logo source">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'upload'}
            className={`profile-doc-source-tab${tab === 'upload' ? ' profile-doc-source-tab--active' : ''}`}
            onClick={() => setTab('upload')}
            disabled={saving}
          >
            Upload
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'link'}
            className={`profile-doc-source-tab${tab === 'link' ? ' profile-doc-source-tab--active' : ''}`}
            onClick={() => setTab('link')}
            disabled={saving}
          >
            Link
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'library'}
            className={`profile-doc-source-tab${tab === 'library' ? ' profile-doc-source-tab--active' : ''}`}
            onClick={() => setTab('library')}
            disabled={saving}
          >
            My Uploads
          </button>
        </div>

        <div className="profile-doc-source-panel">
          {tab === 'upload' ? (
            <>
              <button
                type="button"
                className="profile-doc-file-drop"
                onClick={() => !saving && fileInputRef.current?.click()}
                disabled={saving}
              >
                <span className="profile-doc-file-drop-icon" aria-hidden>
                  +
                </span>
                <span className="profile-doc-file-drop-label">
                  {selectedFile ? selectedFile.name : 'Choose image'}
                </span>
                <span className="profile-doc-file-drop-hint">
                  {selectedFile ? 'Click to replace' : 'PNG, JPG, GIF, WebP, or SVG'}
                </span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                className="profile-doc-file-input"
                disabled={saving}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) setSelectedFile(file);
                }}
              />
            </>
          ) : null}

          {tab === 'link' ? (
            <input
              type="url"
              className="profile-sheet-input"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://…"
              disabled={saving}
            />
          ) : null}

          {tab === 'library' ? (
            <div className="groups-logo-picker-library">
              {!ugpId ? (
                <p className="profile-sheet-hint">
                  Sign in with a role to see images from My Uploads.
                </p>
              ) : docsLoading ? (
                <p className="profile-sheet-hint">Loading My Uploads…</p>
              ) : docs.length === 0 ? (
                <p className="profile-sheet-hint">
                  No images in My Uploads yet. Use the Upload tab, or add one under Attachments
                  on your profile.
                </p>
              ) : (
                <ul className="groups-logo-picker-doc-list" role="listbox" aria-label="My Uploads">
                  {docs.map((doc) => {
                    const active = String(doc.id) === String(selectedDocId);
                    return (
                      <li key={doc.id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={active}
                          className={`groups-logo-picker-doc${active ? ' is-selected' : ''}`}
                          onClick={() => setSelectedDocId(String(doc.id))}
                          disabled={saving}
                        >
                          {doc.url ? (
                            <img
                              className="groups-logo-picker-doc-thumb"
                              src={doc.url}
                              alt=""
                            />
                          ) : (
                            <span className="groups-logo-picker-doc-thumb" aria-hidden />
                          )}
                          <span className="groups-logo-picker-doc-text">
                            <span className="groups-logo-picker-doc-title">
                              {doc.title || doc.file_name || 'Image'}
                            </span>
                            {doc.file_name ? (
                              <span className="groups-logo-picker-doc-meta">{doc.file_name}</span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {localError ? <div className="profile-sheet-error">{localError}</div> : null}
    </ProfileFormSheet>
  );
};

export default GroupLogoPickerSheet;
